<template>
  <div v-if="authenticated">
    <section class="hero is-outlined banner">
      <div class="hero-head">
        <b-select v-model="language" @input="languageChanged">
          <option value="en">English</option>
          <option value="fr">Français</option>
          <option value="es">Español</option>
          <option value="nl">Nederlands</option>
        </b-select>
      </div>
      <div class="hero-body">
        <div class="container">
          <img class="banner-image" src="../assets/sound4x.png" />
          <h2 class="subtitle has-text-white">
            {{ $t('message.explanation') }}
          </h2>
        </div>
      </div>
    </section>

    <div class="container">
      <section class="section">
        <b-field :label="$t('message.station_label')" horizontal>
          <b-field
            :type="getInputType($v.station)"
            :message="getErrorMessages($v.station)"
          >
            <b-autocomplete
              rounded
              v-model="stationText"
              field="station"
              :loading="isFetching"
              :data="stations"
              @typing="getStations"
              placeholder="Please input a station..."
              icon="magnify"
              @select="onSelectStation"
              :input="$v.station.$touch"
            >
              <template slot="empty">No results found</template>
              <template slot-scope="props">
                <div class="media">
                  <div class="media-content">
                    {{ props.option.station }}
                    <br />
                    <small>
                      {{ props.option.network + '.' + props.option.station }}
                    </small>
                  </div>
                </div>
              </template>
            </b-autocomplete>
          </b-field>
        </b-field>

        <b-field horizontal :label="$t('message.channel_label')">
          <b-field
            :type="getInputType($v.channel)"
            :message="getErrorMessages($v.channel)"
          >
            <b-select
              :placeholder="channelPlaceholder"
              rounded
              required
              v-model="channel"
              @input="onChannelSelected"
            >
              <option v-for="option in channels" :value="option" :key="option">
                {{ option }}
              </option>
            </b-select>
          </b-field>
        </b-field>

        <b-field>
          <section class="event-picker">
            <h2 class="title">Events</h2>
            <div ref="list" class="event-list">
              <table class="table is-fullwidth is-striped is-hoverable">
                <tbody>
                  <template
                    v-if="
                      globalEvents.length > 0 ||
                      regionalEvents.length > 0 ||
                      localEvents.length > 0
                    "
                  >
                    <template v-if="localEvents.length > 0">
                      <tr>
                        <th><h4 class="title is-4">Local</h4></th>
                      </tr>
                      <tr
                        v-for="event in localEvents"
                        @click="onEventSelected(event)"
                        :key="event.id"
                        :id="event.name"
                        :class="{ 'is-selected': selectedEvent == event }"
                      >
                        <td>
                          {{ event.location }} -- {{ event.mag }} --
                          {{ event.time.locale(language).format('llll') }}
                        </td>
                      </tr>
                    </template>
                    <template v-if="regionalEvents.length > 0">
                      <tr>
                        <th><h4 class="title is-4">Regional</h4></th>
                      </tr>
                      <tr
                        v-for="event in regionalEvents"
                        @click="onEventSelected(event)"
                        :key="event.id"
                        :id="event.name"
                        :class="{ 'is-selected': selectedEvent == event }"
                      >
                        <td>
                          {{ event.location }} -- {{ event.mag }} --
                          {{ event.time.locale(language).format('llll') }}
                        </td>
                      </tr>
                    </template>
                    <template v-if="globalEvents.length > 0">
                      <tr>
                        <th><h4 class="title is-4">Global</h4></th>
                      </tr>
                      <tr
                        v-for="event in globalEvents"
                        :key="event.id"
                        @click="onEventSelected(event)"
                        :id="event.name"
                        :class="{ 'is-selected': selectedEvent == event }"
                      >
                        <td>
                          {{ event.location }} -- {{ event.mag }} --
                          {{ event.time.locale(language).format('llll') }}
                        </td>
                      </tr>
                    </template>
                  </template>
                  <template v-else-if="station === null">
                    <tr>
                      <td>Please select a station first.</td>
                    </tr>
                  </template>
                  <template v-else>
                    <tr>
                      <td>No events available.</td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </section>
        </b-field>

        <b-field horizontal :label="$t('message.trace_startdate_label')">
          <b-field
            :type="getInputType($v.traceDate)"
            :message="getErrorMessages($v.traceDate)"
          >
            <b-datepicker
              v-model="traceDate"
              placeholder="Click to select..."
              icon="calendar-today"
              :max-date="now"
              @input="$v.traceDate.$touch()"
            />
          </b-field>
        </b-field>

        <b-field horizontal :label="$t('message.trace_starttime_label')">
          <b-field
            :type="getInputType($v.traceTime)"
            :message="getErrorMessages($v.traceTime)"
          >
            <b-timepicker
              v-model="traceTime"
              placeholder="Type or select a date..."
              icon="clock"
              :readonly="false"
              @input="$v.traceTime.$touch()"
            />
          </b-field>
        </b-field>

        <b-field horizontal :label="$t('message.trace_length_label')">
          <b-input
            v-model="traceLength"
            type="number"
            min="100"
            max="1000"
            @input="$v.traceLength.$touch()"
          />
        </b-field>

        <b-field
          horizontal
          :label="$t('message.speed_label')"
          :message="getErrorMessages($v.traceSpeed)"
        >
          <b-radio
            v-model="traceSpeed"
            native-value="1"
            @input="$v.traceSpeed.$touch()"
          >
            {{ $t('message.speed_x1_label') }}
          </b-radio>
          <b-radio
            v-model="traceSpeed"
            native-value="2"
            @input="$v.traceSpeed.$touch()"
          >
            {{ $t('message.speed_x2_label') }}
          </b-radio>
          <b-radio
            v-model="traceSpeed"
            native-value="4"
            @input="$v.traceSpeed.$touch()"
          >
            {{ $t('message.speed_x4_label') }}
          </b-radio>
        </b-field>

        <b-field horizontal>
          <b-field
            :type="getInputType($v.captcha)"
            :message="getErrorMessages($v.captcha)"
          >
            <vue-recaptcha
              ref="recaptcha"
              sitekey="6LeJHUQUAAAAAOPiEf1E7nY4Nf5qDxzOLONU4rLu"
              @verify="onVerify"
            />
          </b-field>
        </b-field>

        <b-field horizontal>
          <!-- Label left empty for spacing -->
          <p class="control">
            <button
              class="button is-primary"
              @click="generateSound"
            >
              {{ $t('message.download_label') }}
            </button>
          </p>
        </b-field>
      </section>
    </div>
    <b-modal :active.sync="isLoading">
      <b-notification type="is-info" has-icon :closable="false">
        <p>Generating {{ progress_count }}/{{ progress_total }}...</p>
        <progress class="progress is-primary" :value="progress" max="100">
          {{ progress }}%
        </progress>
      </b-notification>
    </b-modal>
    <!-- -<b-loading :active.sync="isLoading" /> -->
  </div>
  <div v-else>Connecting to ShakeNet...</div>
</template>

<script lang="js">
import * as fdsn from '../services/fdsn-service.js'
import moment from 'moment'
import VueRecaptcha from 'vue-recaptcha'
import { required, maxValue, minLength } from 'vuelidate/lib/validators'
import { mapState } from 'vuex'

export default {
  name: 'HelloWorld',
  components: {
    VueRecaptcha
  },
  data () {
    return {
      now: moment.utc().toDate(),
      stationText: '',
      stations: [],
      station: null,
      channel: null,
      isFetching: false,
      channels: [],
      traceDate: null,
      traceTime: null,
      maxStartTime: moment.utc().toDate(),
      traceLength: null,
      traceSpeed: 1,
      captcha: null,
      language: 'en',
      isLoading: false,
      progress: 0,
      progress_count: 0,
      progress_total: 0,
      globalEvents: [],
      regionalEvents: [],
      localEvents: [],
      selectedEvent: null
    }
  },
  validations: {
    captcha: {
      required
    },
    station: {
      required,
      minLength: minLength(5)
    },
    channel: {
      required
    },
    traceDate: {
      required,
      maxValue: maxValue(moment.utc().add(1, 'd').toDate())
    },
    traceTime: {
      required,
      // eslint-disable-next-line no-unused-vars
      maxValue (value) {
        if (this.traceDate && this.traceTime) {
          var start = moment.utc()
          start.date(this.traceDate.getDate())
          start.month(this.traceDate.getMonth())
          start.year(this.traceDate.getFullYear())
          start.minutes(this.traceTime.getMinutes())
          start.hours(this.traceTime.getHours())
          console.log('start: ', start.format())
          console.log('now: ', moment.utc(this.now).format())
          return start.isBefore(this.now)
        }
        return true
      }
    },
    traceLength: {
      required,
      maxValue: 1000
    },
    traceSpeed: {
      required
    }
  },
  computed: {
    ...mapState({
      authenticated: state => state.login.authenticated
    }),
    channelPlaceholder () {
      return this.station ? 'Select a channel' : 'Select a station first'
    },
  },
  methods: {
    clamp (value, min, max) {
      return Math.min(Math.max(value, min), max)
    },
    getInputType (input) {
      if (input.$error) {
        return 'is-danger'
      } else if (!input.$invalid) {
        return 'is-success'
      } else {
        return ''
      }
    },
    getErrorMessages (input) {
      var messages = []
      if (!input.$error) {
        return messages
      }
      if (!input.required) {
        messages.push('This value is required')
      }
      if ('minLength' in input && !input.minLength) {
        messages.push('Station name should have 5 characters')
      }
      if ('maxValue' in input && !input.maxValue) {
        messages.push('Please select a value in the past')
      }
      return messages
    },
    onSelectStation (value) {
      this.station = value
      this.channel = null
      console.log('this.station: ', this.station)
      if (this.station) {
        console.log('getting channels')
        fdsn.getChannels(this.station.station)
          .then((channels) => {
            console.log('got channels: ', channels)
            this.channels = channels
            this.channel = this.channels[0]
          })
        this.loadEvents()
      }
    },
    // eslint-disable-next-line no-unused-vars
    onChannelSelected (value) {
    },
    onEventSelected (event) {
      this.selectedEvent = event
      this.traceDate = event.time.toDate()
      this.traceTime = event.time.toDate()
    },
    onVerify (response) {
      this.captcha = response
    },
    onExpired () {
      this.captcha = null
    },
    // eslint-disable-next-line no-unused-vars
    generateSound (useCaptcha) {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }

      var start = moment.utc()
      start.date(this.traceDate.getDate())
      start.month(this.traceDate.getMonth())
      start.year(this.traceDate.getFullYear())
      start.minutes(this.traceTime.getMinutes())
      start.hours(this.traceTime.getHours())

      var params = {
        station: this.station.station,
        channel: this.channel,
        trace: start.format('YYYY-MM-DD HH:mm:ss'),
        length: this.traceLength,
        speed: this.traceSpeed,
        lang: this.language,
        captcha: this.captcha
      }

      this.isLoading = true
      this.$socket.client.emit('get_wave', params)
    },
    languageChanged () {
      this.$i18n.locale = this.language
    },
    getStations () {
      if (this.stationText.length > 1) {
        this.isFetching = true
        fdsn.getStations(this.stationText).then((response) => {
          var stations = response.filter((option, pos) => {
            return option.station &&
              option.station.toLowerCase().indexOf(this.stationText.toLowerCase()) >= 0 &&
              response.findIndex(s => s.station === option.station) === pos
          })
          this.stations = stations
          this.isFetching = false
        })
      }
    },
    loadEvents () {
      var lastMonth = moment().subtract(1, 'M').format('Y-MM-DDTHH:mm:ss')
      var globalParams = {
        start: lastMonth,
        minmag: 6.5
      }
      var regionalParams = {
        maxlat: fdsn.clamp(this.station.latitude + 10, -90, 90),
        maxlon: fdsn.clamp(this.station.longitude + 10, -180, 180),
        minlat: fdsn.clamp(this.station.latitude - 10, -90, 90),
        minlon: fdsn.clamp(this.station.longitude - 10, -180, 180),
        start: lastMonth,
        minmag: 4.5
      }
      var localParams = {
        maxlat: fdsn.clamp(this.station.latitude + 1, -90, 90),
        maxlon: fdsn.clamp(this.station.longitude + 1, -180, 180),
        minlat: fdsn.clamp(this.station.latitude - 1, -90, 90),
        minlon: fdsn.clamp(this.station.longitude - 1, -180, 180),
        start: lastMonth
      }

      fdsn.getEvents(globalParams).then(events => {
        this.globalEvents = events
        this.globalEvents.sort((a, b) => b.time.valueOf() - a.time.valueOf())
      })

      fdsn.getEvents(regionalParams).then(events => {
        this.regionalEvents = events
        this.regionalEvents.sort((a, b) => b.time.valueOf() - a.time.valueOf())
      })

      fdsn.getEvents(localParams).then(events => {
        this.localEvents = events
        this.localEvents.sort((a, b) => b.time.valueOf() - a.time.valueOf())
      })
    }
  },
  sockets: {
    connect () {
      console.log('socket connected')
    },
    progress_wave (data) {
      this.progress_count = data.count
      this.progress_total = data.total
      this.progress = (this.progress_count / this.progress_total) * 100
    },
    error_wave (data) {
      this.isLoading = false
      this.progress_count = 0
      this.progress_total = 0
      this.progress = 0
      this.$buefy.toast.open({
        duration: 5000,
        message: data.error,
        type: 'is-danger'
      })
    },
    complete_wave (data) {
      this.isLoading = false
      this.progress_count = 0
      this.progress_total = 0
      this.progress = 0
      location.href = '/get_file/' + data.filepath
      this.$refs.recaptcha.reset()
    }
  },
  created () {
  //  fdsn.getStations('').then((response) => {
  //    this.stations = response
  //    this.isFetching = false
  //  })
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
$primary: #0e2d46;

h1,
h2 {
  font-weight: normal;
}
ul {
  list-style-type: none;
  padding: 0;
}
li {
  display: inline-block;
  margin: 0 10px;
}
a {
  color: #42b983;
}

.banner {
  background-color: #0e2d46;
}

.banner-image {
  width: 40%;
  margin-bottom: 2rem;
}
</style>
